import { Box } from "@mui/material";
import { Layout } from "react-grid-layout";
import { useTranslation } from "react-i18next";

import {
  AndonBoardLayoutItem,
  AndonCellBorder,
  AndonDisplayLine,
  AndonGridCell,
  BoardDimensions,
  BorderSide,
  WorkingLayout
} from "~/features/andon/andon.types";
import {
  isWorkstationLayoutItem,
  mergeGridLayoutChange
} from "~/features/andon/andon.utils";
import { computeAndonTileFontSize } from "~/features/andon/andonBoard/andonBoardLayout.utils";
import { WorkstationSummaryDto } from "~/types/api";

import AndonBoardElement from "./AndonBoardElement";
import AndonCellEditTriage from "./AndonCellEditTriage";
import BorderOverlay from "./BorderOverlay";
import DragAndDropGridLayout from "./DragAndDropGridLayout";
import SelectionOverlay from "./SelectionOverlay";

type AndonBoardGridProps = {
  boardRef: (element: HTMLElement | null) => void;
  isFullScreen: boolean;
  editModeEnabled: boolean;
  enableAndonBorders: boolean;
  isMarqueeDragging: boolean;
  boardWidth?: number;
  boardHeight?: number;
  gridRowHeight: number;
  boardDimensions: BoardDimensions;
  cellBorders: AndonCellBorder[];
  selectionHighlightCells: AndonGridCell[];
  workingLayoutItems: AndonBoardLayoutItem[];
  boardScale: number;
  boardIconScale: number;
  boardDisplayLines: AndonDisplayLine[];
  boardWorkstationsReady: boolean;
  workstations?: WorkstationSummaryDto[];
  workstationFcAbbreviationById: Record<string, string | undefined>;
  setWorkingLayout: React.Dispatch<React.SetStateAction<WorkingLayout>>;
  onBoardMouseDown: (event: React.MouseEvent<HTMLElement>) => void;
  onExistingElementClick: (
    event: React.MouseEvent<HTMLElement>,
    index: string
  ) => void;
  onGridClick: (event: MouseEvent) => void;
  removeWorkstation: (workstationId: string) => void;
  isMultiCellSelection: boolean;
  isTextLabelSelected: boolean;
  isEditCellMenuOpen: boolean;
  editCellMenuAnchorPosition: { left: number; top: number } | null;
  selectedGridIndex: string | null;
  targetCellSides: BorderSide[];
  targetCellColor: string;
  pickerSelectionTitle?: string;
  workingWorkstationIds: string[];
  siteFcId?: string;
  closeEditCellMenu: (
    event?: object | React.SyntheticEvent | Event | null,
    reason?: string
  ) => void;
  handleSetCellBorder: (sides: BorderSide[], color: string) => void;
  handleEditTextLabel: (layoutItemId: string) => void;
  handleDeleteTextLabel: (layoutItemId: string) => void;
  selectWorkstation: (
    workstationId: string | null,
    fulfillmentCenterId?: string
  ) => void;
  handlePickWorkstation: (
    workstationId: string,
    fulfillmentCenterId?: string
  ) => void;
  openAddTextLabelModal: () => void;
};

export default function AndonBoardGrid({
  boardRef,
  isFullScreen,
  editModeEnabled,
  enableAndonBorders,
  isMarqueeDragging,
  boardWidth,
  boardHeight,
  gridRowHeight,
  boardDimensions,
  cellBorders,
  selectionHighlightCells,
  workingLayoutItems,
  boardScale,
  boardIconScale,
  boardDisplayLines,
  boardWorkstationsReady,
  workstations,
  workstationFcAbbreviationById,
  setWorkingLayout,
  onBoardMouseDown,
  onExistingElementClick,
  onGridClick,
  removeWorkstation,
  isMultiCellSelection,
  isTextLabelSelected,
  isEditCellMenuOpen,
  editCellMenuAnchorPosition,
  selectedGridIndex,
  targetCellSides,
  targetCellColor,
  pickerSelectionTitle,
  workingWorkstationIds,
  siteFcId,
  closeEditCellMenu,
  handleSetCellBorder,
  handleEditTextLabel,
  handleDeleteTextLabel,
  selectWorkstation,
  handlePickWorkstation,
  openAddTextLabelModal
}: AndonBoardGridProps) {
  const { t } = useTranslation();

  return (
    <Box
      ref={boardRef}
      onMouseDown={onBoardMouseDown}
      sx={{
        width: "100%",
        height: isFullScreen ? undefined : "70vh",
        flex: isFullScreen ? 1 : undefined,
        minHeight: isFullScreen ? 0 : undefined,
        position: "relative",
        ...(editModeEnabled
          ? {
              cursor: isMarqueeDragging
                ? "move"
                : enableAndonBorders
                  ? "cell"
                  : "default",
              "& .andon-board": {
                cursor: isMarqueeDragging
                  ? "move"
                  : enableAndonBorders
                    ? "cell"
                    : "default"
              },
              "& .react-grid-item": {
                cursor: isMarqueeDragging ? "move" : "grab"
              },
              "& .react-grid-item.react-draggable-dragging": {
                cursor: "grabbing"
              },
              ...(isMarqueeDragging
                ? {
                    "& .react-grid-item, & .react-grid-item *": {
                      cursor: "move !important"
                    }
                  }
                : {})
            }
          : {
              cursor: "default",
              "& .andon-board, & .react-grid-item, & .react-grid-item *": {
                cursor: "default !important"
              }
            })
      }}
      role="region"
      aria-label={t("andon board")}
    >
      <BorderOverlay
        columns={boardDimensions.columns}
        rows={boardDimensions.rows}
        rowPitch={gridRowHeight}
        cellBorders={cellBorders}
      />
      <SelectionOverlay
        columns={boardDimensions.columns}
        rows={boardDimensions.rows}
        rowPitch={gridRowHeight}
        selectedCells={selectionHighlightCells}
      />
      {boardWidth && boardHeight ? (
        <DragAndDropGridLayout
          sizeLocked={!editModeEnabled}
          isDraggable={editModeEnabled}
          className="andon-board"
          layout={workingLayoutItems}
          cols={boardDimensions.columns}
          width={boardWidth}
          rowHeight={gridRowHeight}
          onLayoutChange={(layout: Layout[]) =>
            setWorkingLayout((currentWorkingLayout) => ({
              ...currentWorkingLayout,
              layout: mergeGridLayoutChange(
                currentWorkingLayout.layout,
                layout,
                currentWorkingLayout.dimensions.rows
              )
            }))
          }
          preventCollision
          onExistingElementClick={onExistingElementClick}
          onGridClick={onGridClick}
          renderElementWithIndex={(elementId) => {
            const matchingLayout = workingLayoutItems.find(
              (boardLayoutItem) => boardLayoutItem.i === elementId
            );
            if (!matchingLayout) return null;

            const heightSpan = matchingLayout.h || 1;
            const fontSize = computeAndonTileFontSize({
              boardHeight,
              heightSpan,
              rows: boardDimensions.rows,
              boardScale
            });

            return (
              <AndonBoardElement
                boardLayoutItem={matchingLayout}
                boardScale={boardScale}
                workstation={
                  boardWorkstationsReady &&
                  isWorkstationLayoutItem(matchingLayout)
                    ? workstations?.find(
                        (workstation) => workstation.id === elementId
                      )
                    : undefined
                }
                workstationsReady={boardWorkstationsReady}
                fontSize={fontSize}
                iconScale={boardIconScale}
                displayLines={boardDisplayLines}
                fcAbbreviation={workstationFcAbbreviationById[elementId]}
                onRemoveWorkstation={
                  editModeEnabled
                    ? () => removeWorkstation(elementId)
                    : undefined
                }
              />
            );
          }}
        />
      ) : null}

      <AndonCellEditTriage
        enableAndonBorders={enableAndonBorders}
        isMultiCellSelection={isMultiCellSelection}
        isTextLabelSelected={isTextLabelSelected}
        isEditCellMenuOpen={isEditCellMenuOpen}
        editCellMenuAnchorPosition={editCellMenuAnchorPosition}
        selectedGridIndex={selectedGridIndex}
        targetCellSides={targetCellSides}
        targetCellColor={targetCellColor}
        pickerSelectionTitle={pickerSelectionTitle}
        workingWorkstationIds={workingWorkstationIds}
        siteFcId={siteFcId}
        closeEditCellMenu={closeEditCellMenu}
        handleSetCellBorder={handleSetCellBorder}
        handleEditTextLabel={handleEditTextLabel}
        handleDeleteTextLabel={handleDeleteTextLabel}
        selectWorkstation={selectWorkstation}
        handlePickWorkstation={handlePickWorkstation}
        openAddTextLabelModal={openAddTextLabelModal}
      />
    </Box>
  );
}
